import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, message, WkdUploadCore, Chips, Popover, Checkbox } from '@common/musicHubCommon';
import { useUserStore, getArtistID, getLoginType } from '../../../store';
import { ROUTES } from '../../../routes';
import { PHONE_PREFIXES, COUNTRIES, ARTIST_ROLES, SOCIAL_PLATFORMS } from './constant';
import { getMusicGenre, createUserAccount } from '../../../services/index';
import { reportPageView } from '../../../services/reportLog';
import { debounce } from '../../../common/debounce';
import './index.scss';



const OnboardingProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser, setRegistered, logout } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  
  // 表单状态
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [uploading, setUploading] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [country, setCountry] = useState<string>();
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [genres, setGenres] = useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [socialLinks, setSocialLinks] = useState<Array<{ platform: string; url: string }>>([
    { platform: '', url: '' }
  ]);

  useEffect(() => {
    reportPageView('content_0006', { withArtistId: true });
  }, []);

  useEffect(() => {
    getMusicGenre().then(res => {
      console.log(res.genreMap);
      const genreMap = res?.genreMap;
      if(genreMap && Object.keys(genreMap).length > 0) {
        setGenres(Object.keys(genreMap).map((item: any) => ({ label: genreMap[item], value: item })));
      }
    });
  }, []);

  // 验证必填项
  const isFormValid = useMemo(() => {
    return avatarUrl && phonePrefix && phoneNumber && country;
  }, [avatarUrl, phonePrefix, phoneNumber, country]);

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleAvatarUpload = (fileInfo: any) => {
    setUploading(false);
    if (fileInfo?.url) {
      setAvatarUrl(fileInfo.url);
    }
  };

  const handleBeforeUpload = () => {
    setUploading(true);
    return Promise.resolve();
  };

  const handleUploadFail = (error: any) => {
    setUploading(false);
    message.error(String(error));
  };

  const handleAddSocialLink = () => {
    setSocialLinks(prev => [...prev, { platform: '', url: '' }]);
  };

  const handleSocialLinkChange = (index: number, field: 'platform' | 'url', value: string) => {
    setSocialLinks(prev => {
      const newLinks = [...prev];
      newLinks[index] = { ...newLinks[index], [field]: value };
      return newLinks;
    });
  };

  const handleRemoveSocialLink = (index: number) => {
    setSocialLinks(prev => prev.filter((_, i) => i !== index));
  };

  const submitProfile = async (values: any) => {
    if (!isFormValid) {
      message.error('Please fill in all required fields');
      return;
    }
    if (!agreedTerms) {
      message.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    
    try {
      // 调用后端注册接口
      await createUserAccount({
        uid: getArtistID(),
        loginType: getLoginType() || 1,
        email: user?.email || '',
        stageName: user?.name || '',
        profilePicture: avatarUrl || '',
        whatsappPrefix: phonePrefix || '',
        whatsappNumber: phoneNumber || '',
        country: country || '',
        bio: values.bio || '',
        label: values.recordLabel || '',
        genre: selectedGenres.map(g => Number(g)),
        role: selectedRoles,
        mediaLinks: socialLinks.reduce((acc, link) => {
          if (link.platform && link.url) {
            acc[link.platform] = link.url;
          }
          return acc;
        }, {} as Record<string, string>),
      });

      // 更新本地用户数据
      updateUser({
        avatar: avatarUrl,
        country: country,
        genres: selectedGenres,
        bio: values.bio,
        socialLinks: socialLinks.reduce((acc, link) => {
          if (link.platform && link.url) {
            acc[link.platform] = link.url;
          }
          return acc;
        }, {} as Record<string, string>),
      });

      setRegistered(true);
      message.success('Profile created successfully!');
      navigate(ROUTES.dashboard);
    } catch (error: any) {
      console.error('Failed to create account:', error);
      message.error(error?.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const debouncedSubmit = useMemo(
    () => debounce((values: any) => { void submitProfile(values); }, 300),
    [submitProfile],
  );

  const handleSubmit = (values: any) => {
    debouncedSubmit(values);
  };

  useEffect(() => {
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit]);

  // 返回上一步
  const handleBack = () => {
    navigate(ROUTES.onboardingName);
  };

  // 登出
  const handleLogout = () => {
    logout();
    window.location.href = ROUTES.login;
  };

  // 用户菜单内容
  const userMenuContent = (
    <div className="user-menu">
      <button className="user-menu__item" onClick={handleLogout}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
        </svg>
        <span>Sign out</span>
      </button>
    </div>
  );

  return (
    <div className="onboarding-profile">
      {/* 顶部导航栏 */}
      <div className="onboarding-profile__nav">
        <button className="nav-back" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          <span>Create Artist Profile</span>
        </button>
        
        <Popover content={userMenuContent} trigger="click" placement="bottomRight">
          <button className="nav-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt="Avatar" />
            ) : (
              <img src="https://akoss.bangcdn.net/fe/User.svg" alt="" width={24} height={24} />
            )}
          </button>
        </Popover>
      </div>

      {/* 顶部 Header */}
      <div className="onboarding-profile__header">
        <div className="greeting">
          <span className="greeting__hi">Hi,</span>
          <span className="greeting__name">{user?.name || 'Artist'}</span>
        </div>
        <p className="subtitle">Please add more information to complete your profile</p>
      </div>

      {/* 表单区域 */}
      <div className="onboarding-profile__form">
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          
          {/* 头像上传 */}
          <Form.Item 
            label={<span className="form-label">Profile Picture <span className="required">*</span></span>}
          >
            <div className="avatar-upload">
              <WkdUploadCore
                accept="image/jpeg,image/png"
                size={10 * 1024}
                // ratio={1}
                onBeforeUpload={handleBeforeUpload}
                onChange={handleAvatarUpload}
                onFaild={handleUploadFail}
              >
                <div className="avatar-upload__trigger">
                  {uploading ? (
                    <div className="avatar-upload__loading">
                      <div className="avatar-upload__spinner" />
                    </div>
                  ) : avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="avatar-upload__preview" />
                  ) : (
                    <div className="avatar-upload__placeholder">
                      <img src="https://akoss.bangcdn.net/fe/Upload.svg" alt="" width={32} height={32} />
                    </div>
                  )}
                </div>
              </WkdUploadCore>
              <span className="avatar-hint">Min 800×800 pixels, JPG or PNG. Maximum 10 MB.</span>
            </div>
          </Form.Item>

          {/* WhatsApp Number */}
          <Form.Item 
            label={<span className="form-label">WhatsApp Number <span className="required">*</span></span>}
          >
            <div className="phone-input">
              <Select 
                placeholder="Select"
                options={PHONE_PREFIXES}
                value={phonePrefix}
                onChange={setPhonePrefix}
                className="phone-prefix"
                status={phoneNumber && !phonePrefix ? 'error' : undefined}
              />
              <Input 
                placeholder="Enter number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="phone-number"
              />
            </div>
            {phoneNumber && !phonePrefix && (
              <div className="phone-error">Please select the area code</div>
            )}
          </Form.Item>

          {/* Country/Region */}
          <Form.Item 
            label={<span className="form-label">Country/Region <span className="required">*</span></span>}
          >
            <Select 
              placeholder="Select"
              options={COUNTRIES}
              value={country}
              onChange={setCountry}
            />
          </Form.Item>

          {/* Bio */}
          <Form.Item name="bio">
            <Input.TextArea 
              label="Bio"
              rows={4} 
              placeholder="Describe your music style & story..." 
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* Record Label */}
          <Form.Item 
            name="recordLabel" 
            label={<span className="form-label">Record Label</span>}
          >
            <Input placeholder="Leave empty if Independent" />
          </Form.Item>

          {/* Music Genre */}
          <Form.Item label={<span className="form-label">Music Genre</span>}>
            <div className="chips-container">
              {genres.map(genre => (
                <Chips
                  key={genre.value}
                  selected={selectedGenres.includes(genre.value)}
                  onClick={() => handleGenreToggle(genre.value)}
                >
                  {genre.label}
                </Chips>
              ))}
            </div>
          </Form.Item>

          {/* Artist Role */}
          <Form.Item label={<span className="form-label">Artist Role</span>}>
            <div className="chips-container">
              {ARTIST_ROLES.map(role => (
                <Chips
                  key={role.value}
                  selected={selectedRoles.includes(role.value)}
                  onClick={() => handleRoleToggle(role.value)}
                >
                  {role.label}
                </Chips>
              ))}
            </div>
          </Form.Item>

          {/* Social Media Links */}
          <Form.Item label={<span className="form-label">Social Media Links</span>}>
            <div className="social-links">
              {socialLinks.map((link, index) => (
                <div key={index} className="social-link-row">
                  <Select 
                    placeholder="Select"
                    options={SOCIAL_PLATFORMS}
                    value={link.platform || undefined}
                    onChange={(val) => handleSocialLinkChange(index, 'platform', val)}
                    className="social-platform"
                  />
                  <div className="social-url-wrap">
                    <Input 
                      placeholder="Paste Profile Link"
                      value={link.url}
                      onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                      className="social-url"
                    />
                    {index > 0 && (
                      <button
                        type="button"
                        className="social-link-remove"
                        onClick={() => handleRemoveSocialLink(index)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button 
                type="button" 
                className="add-social-btn"
                onClick={handleAddSocialLink}
              >
                + Add Social Media
              </button>
            </div>
          </Form.Item>

          {/* 协议勾选 */}
          <Form.Item className="terms-consent-item">
            <div className="terms-consent">
              <div className="terms-consent__title">
                Terms and Conditions <span className="required">*</span>
              </div>
              <Checkbox checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)}>
                By creating an account, you agree to Phoenix Music&apos;s{' '}
                <a href={`${window.location.origin}/terms.html`} target="_blank" rel="noreferrer">
                  [Terms of Service]
                </a>{' '}
                and{' '}
                <a href="https://phoenix-browser.com/privacy-policy.html" target="_blank" rel="noreferrer">
                  [Privacy Policy]
                </a>
                .
              </Checkbox>
            </div>
          </Form.Item>

          {/* 提交按钮 */}
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              disabled={!isFormValid || !agreedTerms}
              className={`submit-btn ${!isFormValid || !agreedTerms ? 'submit-btn--disabled' : ''}`}
            >
              Create Artist Profile
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default OnboardingProfile;
